import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ContentSource, Section, VisibilityType, VISIBILITY_LABELS, ReviewStatusType, REVIEW_STATUS_LABELS } from '../types/cms';
import { reviewApi, sectionApi } from '../services/cmsApi';
import TiptapEditor from '../components/TiptapEditor';
import DiffView from '../components/DiffView';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { Edit, MoreVertical, Save } from 'lucide-react';
import { extractPlainTextFromTipTap } from '../utils/tipTapUtils';
import ProcessChecklist, { ReviewChecklistItem, ChecklistItem } from '../components/ProcessChecklist';
import { getAllMetadataTypes } from '../services/adminApi';
import { MetadataType } from '../services/adminApi';
import { contentSourceApi } from '../services/cmsApi';
import './ReviewEditor.css';

interface SearchResult {
    section: Section;
    contentSource: ContentSource;
    breadcrumb: string[];
    matchText: string;
}

const ReviewEditor: React.FC = () => {
    const { dialogState, showConfirm } = useConfirmDialog();
    const [assignedContentSources, setAssignedContentSources] = useState<ContentSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedSection, setSelectedSection] = useState<SearchResult | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingContent, setEditingContent] = useState<string>('');
    const [isEditingOriginal, setIsEditingOriginal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [sectionMetadata, setSectionMetadata] = useState<{
        visibility?: VisibilityType;
        ref_visibility: VisibilityType;
        filter_tags: string[];
        review_status: ReviewStatusType;
    } | null>(null);
    
    // Review checklist state
    const [reviewChecklist, setReviewChecklist] = useState<ReviewChecklistItem[]>([]);
    const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);

    // Fetch assigned content sources
    useEffect(() => {
        const fetchAssigned = async () => {
            try {
                setLoading(true);
                setError('');
                const sources = await reviewApi.getAssigned();
                setAssignedContentSources(sources);
            } catch (err: any) {
                setError(err?.response?.data?.message || 'Failed to load assigned content sources');
                console.error('Error fetching assigned content sources:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAssigned();
    }, []);
    
    // Initialize review checklist from assigned ContentSources or fetch defaults
    useEffect(() => {
        const initializeChecklist = async () => {
            // Check if any assigned ContentSource has review checklist
            const sourceWithChecklist = assignedContentSources.find(
                (source) => source.process_metadata?.review?.checklist
            );
            
            if (sourceWithChecklist && sourceWithChecklist.process_metadata?.review?.checklist) {
                setReviewChecklist(sourceWithChecklist.process_metadata.review.checklist);
                return;
            }
            
            // Fetch defaults from MetadataType
            if (assignedContentSources.length > 0) {
                setIsLoadingChecklist(true);
                try {
                    const metadataTypes = await getAllMetadataTypes();
                    const reviewChecklistType = metadataTypes.find(
                        (type: MetadataType) => type.field_name === 'process_metadata_review_checklist'
                    );
                    
                    if (reviewChecklistType && reviewChecklistType.enum_options && Array.isArray(reviewChecklistType.enum_options)) {
                        // Initialize all items with checked: false
                        const defaultChecklist = reviewChecklistType.enum_options.map((item: any) => ({
                            ...item,
                            checked: false
                        }));
                        setReviewChecklist(defaultChecklist);
                    }
                } catch (error) {
                    console.error('Error fetching default review checklist:', error);
                } finally {
                    setIsLoadingChecklist(false);
                }
            }
        };
        
        initializeChecklist();
    }, [assignedContentSources]);
    
    // Handle review checklist update - update ALL assigned ContentSources
    // Use ref to avoid stale closure issue when assignedContentSources changes during async operation
    const assignedContentSourcesRef = useRef<ContentSource[]>(assignedContentSources);
    
    // Keep ref in sync with state
    useEffect(() => {
        assignedContentSourcesRef.current = assignedContentSources;
    }, [assignedContentSources]);
    
    const handleReviewChecklistUpdate = useCallback(async (updatedChecklist: ChecklistItem[]) => {
        // Use ref to get current value (avoids stale closure)
        const currentSources = assignedContentSourcesRef.current;
        
        if (currentSources.length === 0) return;
        
        // Type guard to ensure it's ReviewChecklistItem[]
        const reviewChecklist = updatedChecklist as ReviewChecklistItem[];
        
        setIsLoadingChecklist(true);
        try {
            // Update all assigned ContentSources using current ref value
            const updatePromises = currentSources.map(async (source) => {
                const currentProcessMetadata = source.process_metadata || {};
                const updatedProcessMetadata = {
                    ...currentProcessMetadata,
                    review: {
                        checklist: reviewChecklist
                    }
                };
                
                return contentSourceApi.update(source.id, {
                    process_metadata: updatedProcessMetadata
                });
            });
            
            await Promise.all(updatePromises);
            
            // Update local state
            setReviewChecklist(reviewChecklist);
            
            // Refresh assigned ContentSources to get updated data
            const sources = await reviewApi.getAssigned();
            setAssignedContentSources(sources);
            // Update ref with new data
            assignedContentSourcesRef.current = sources;
        } catch (error) {
            console.error('Error updating review checklist:', error);
            throw error;
        } finally {
            setIsLoadingChecklist(false);
        }
    }, []); // Empty dependency array - uses ref instead

    // Debounced search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                setIsSearching(true);
                const results = await reviewApi.search(searchQuery);
                setSearchResults(results);
            } catch (err: any) {
                console.error('Error searching:', err);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Load section when selected
    useEffect(() => {
        if (selectedSection) {
            const section = selectedSection.section;
            const contentToEdit = isEditingOriginal 
                ? (section.content_json ? JSON.stringify(section.content_json) : '')
                : (section.content_reviewed_json ? JSON.stringify(section.content_reviewed_json) : (section.content_json ? JSON.stringify(section.content_json) : ''));
            
            setEditingContent(contentToEdit);
            setSectionMetadata({
                visibility: section.visibility,
                ref_visibility: section.ref_visibility,
                filter_tags: section.filter_tags || [],
                review_status: section.review_status
            });
        }
    }, [selectedSection, isEditingOriginal]);

    // Reset edit mode only when section changes (not when isEditingOriginal changes)
    useEffect(() => {
        if (selectedSection) {
            setIsEditMode(false);
            setIsEditingOriginal(false);
            setShowOptionsMenu(false);
        }
    }, [selectedSection]);

    // Close options menu when clicking outside
    useEffect(() => {
        if (!showOptionsMenu) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.review-options-menu-container')) {
                setShowOptionsMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOptionsMenu]);

    // Check if there are differences between original and reviewed content
    const hasContentChanges = useMemo(() => {
        if (!selectedSection) return false;
        const section = selectedSection.section;
        
        // If no reviewed content exists, no changes
        if (!section.content_reviewed_json) return false;
        
        // Extract plain text from both
        const originalText = extractPlainTextFromTipTap(section.content_json) || '';
        const reviewedText = extractPlainTextFromTipTap(section.content_reviewed_json) || '';
        
        // Compare texts - if different, there are changes
        return originalText !== reviewedText;
    }, [selectedSection]);

    // Helper function to highlight search terms in text
    const highlightSearchTerms = useCallback((text: string, query: string): React.ReactNode => {
        if (!query || !text) return text;
        
        const queryLower = query.toLowerCase();
        const textLower = text.toLowerCase();
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let index = textLower.indexOf(queryLower, lastIndex);
        
        while (index !== -1) {
            // Add text before match
            if (index > lastIndex) {
                parts.push(text.substring(lastIndex, index));
            }
            
            // Add highlighted match
            parts.push(
                <mark key={index} className="review-search-highlight">
                    {text.substring(index, index + query.length)}
                </mark>
            );
            
            lastIndex = index + query.length;
            index = textLower.indexOf(queryLower, lastIndex);
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }
        
        return parts.length > 0 ? <>{parts}</> : text;
    }, []);

    const handleContentChange = useCallback((content: string) => {
        setEditingContent(content);
    }, []);

    const handleSaveSection = async () => {
        if (!selectedSection) return;

        try {
            setIsSaving(true);
            const section = selectedSection.section;
            const contentJson = editingContent ? JSON.parse(editingContent) : null;

            const updateData: any = {
                filter_tags: sectionMetadata?.filter_tags || [],
                ref_visibility: sectionMetadata?.ref_visibility || 'public',
                review_status: sectionMetadata?.review_status || 'none'
            };

            if (sectionMetadata?.visibility !== undefined) {
                updateData.visibility = sectionMetadata.visibility;
            }

            if (isEditingOriginal) {
                updateData.content_json = contentJson;
            } else {
                updateData.content_reviewed_json = contentJson;
            }

            await sectionApi.update(section.id, updateData);
            
            // Refresh the section data
            const updatedSection = await sectionApi.getById(section.id);
            const updatedSelectedSection = {
                ...selectedSection,
                section: updatedSection
            };
            setSelectedSection(updatedSelectedSection);

            // Update search results to reflect the updated section
            setSearchResults(prevResults => 
                prevResults.map(result => 
                    result.section.id === updatedSection.id
                        ? { ...result, section: updatedSection }
                        : result
                )
            );

            // Refresh assigned content sources to get updated counts
            const sources = await reviewApi.getAssigned();
            setAssignedContentSources(sources);
            
            // Reset editing original state after successful save
            setIsEditingOriginal(false);
        } catch (err: any) {
            console.error('Error saving section:', err);
            alert(err?.response?.data?.message || 'Failed to save section');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCompleteReview = async () => {
        const confirmed = await showConfirm({
            title: 'Complete Review',
            message: 'Are you sure you want to mark all assigned content sources as reviewed? This action cannot be undone.',
            confirmText: 'Complete Review',
            cancelText: 'Cancel',
            isDestructive: false
        });

        if (!confirmed) {
            return;
        }

        try {
            setIsSaving(true);
            await reviewApi.completeReview();
            // Refresh assigned content sources
            const sources = await reviewApi.getAssigned();
            setAssignedContentSources(sources);
            setSelectedSection(null);
            setSearchQuery('');
            setSearchResults([]);
        } catch (err: any) {
            console.error('Error completing review:', err);
            alert(err?.response?.data?.message || 'Failed to complete review');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="review-editor-loading">
                <div className="review-editor-spinner"></div>
                <p>Loading assigned content sources...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="review-editor-error">
                <h2>Error</h2>
                <p>{error}</p>
            </div>
        );
    }

  return (
        <div className="review-editor-container">
            <div className="review-editor-header">
                <div>
                    <h1 className="review-editor-title">Review Content</h1>
                    <p className="review-editor-subtitle">
                        {assignedContentSources.length} content source{assignedContentSources.length !== 1 ? 's' : ''} assigned for review
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {assignedContentSources.length > 0 && (
                        <ProcessChecklist
                            checklist={reviewChecklist}
                            onUpdate={handleReviewChecklistUpdate}
                            title="Review Checklist"
                            isLoading={isLoadingChecklist}
                            type="review"
                        />
                    )}
                    {assignedContentSources.length > 0 && (
                        <button
                            onClick={handleCompleteReview}
                            disabled={isSaving}
                            className="review-complete-button"
                        >
                            {isSaving ? 'Completing...' : 'Review Complete'}
                        </button>
                    )}
                </div>
            </div>

            <div className="review-editor-content">
                {/* Left Sidebar - Content Sources List */}
                <div className={`review-editor-sidebar ${isEditMode ? 'edit-mode' : ''}`}>
                    <div className="review-search-container">
                        <input
                            type="text"
                            placeholder="Search content sources and sections..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="review-search-input"
                        />
                        {isSearching && <div className="review-search-spinner"></div>}
                    </div>

                    {searchQuery ? (
                        <div className="review-search-results">
                            <h3 className="review-section-title">Search Results</h3>
                            {searchResults.length === 0 ? (
                                <div className="review-empty-state">
                                    {isSearching ? 'Searching...' : 'No results found'}
                                </div>
                            ) : (
                                <div className="review-results-list">
                                    {searchResults.map((result, index) => (
                                        <div
                                            key={`${result.section.id}-${index}`}
                                            className={`review-result-item ${selectedSection?.section.id === result.section.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedSection(result)}
                                        >
                                            <div className="review-result-header">
                                                <span className="review-result-source">{result.contentSource.title}</span>
                                            </div>
                                            <div className="review-result-breadcrumb">
                                                {result.breadcrumb.join(' > ')}
                                            </div>
                                            <div className="review-result-section-title">{result.section.title}</div>
                                            <div className="review-result-match">
                                                {highlightSearchTerms(result.matchText.substring(0, 100), searchQuery)}
                                                {result.matchText.length > 100 && '...'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="review-content-sources-list">
                            <h3 className="review-section-title">Assigned Content Sources</h3>
                            {assignedContentSources.length === 0 ? (
                                <div className="review-empty-state">
                                    No content sources assigned for review
                                </div>
                            ) : (
                                <div className="review-sources-list">
                                    {assignedContentSources.map((source) => (
                                        <div key={source.id} className="review-source-item">
                                            <div className="review-source-title">{source.title}</div>
                                            <div className="review-source-meta">
                                                {source.section_count || 0} section{(source.section_count || 0) !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right Panel - Section Editor */}
                <div className="review-editor-main">
                    {selectedSection ? (
                        <div className="review-section-editor">
                            <div className="review-section-header">
                                <div>
                                    <h2 className="review-section-editor-title">{selectedSection.section.title}</h2>
                                    <div className="review-section-breadcrumb">
                                        {selectedSection.contentSource.title} <span className="review-breadcrumb-separator">{'>'}</span> {selectedSection.breadcrumb.map((item, index) => (
                                            <React.Fragment key={index}>
                                                {index > 0 && <span className="review-breadcrumb-separator">{' > '}</span>}
                                                {item}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                                <div className="review-section-actions">
                                    {!isEditMode ? (
                                        <button
                                            onClick={() => setIsEditMode(true)}
                                            className="review-edit-button"
                                            title="Edit section"
                                        >
                                            <Edit size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                            Edit
                                        </button>
                                    ) : (
                                        <>
                                            <div className="review-options-menu-container" style={{ position: 'relative' }}>
                                                <button
                                                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                                                    className="review-options-button"
                                                    title="More options"
                                                >
                                                    <MoreVertical size={16} />
                                                </button>
                                                {showOptionsMenu && (
                                                    <div className="review-options-dropdown">
                                                        <button
                                                            onClick={() => {
                                                                setIsEditingOriginal(!isEditingOriginal);
                                                                setShowOptionsMenu(false);
                                                            }}
                                                            className={`review-options-item ${isEditingOriginal ? 'active' : ''}`}
                                                        >
                                                            {isEditingOriginal ? '✓ Editing Original' : 'Edit Original'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={handleSaveSection}
                                                disabled={isSaving}
                                                className="review-save-button"
                                            >
                                                <Save size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                                {isSaving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsEditMode(false);
                                                    setIsEditingOriginal(false);
                                                    setShowOptionsMenu(false);
                                                }}
                                                className="review-cancel-button"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {isEditMode && isEditingOriginal && (
                                <div className="review-warning-banner">
                                    ⚠️ You are editing the original content. Changes will affect the source content.
                                </div>
                            )}

                            {isEditMode ? (
                                <div className="review-editor-wrapper">
                                    <TiptapEditor
                                        value={editingContent}
                                        onChange={handleContentChange}
                                        placeholder="Section content will appear here..."
                                        editable={true}
                                        searchQuery={searchQuery}
                                    />
                                </div>
                            ) : hasContentChanges ? (
                                <div className="review-diff-view-wrapper">
                            <DiffView
                                originalContent={selectedSection.section.content_json || null}
                                reviewedContent={selectedSection.section.content_reviewed_json || null}
                                viewMode="unified"
                                searchQuery={searchQuery}
                            />
                                </div>
                            ) : (
                                <div className="review-editor-wrapper">
                                    <TiptapEditor
                                        value={selectedSection.section.content_reviewed_json 
                                            ? JSON.stringify(selectedSection.section.content_reviewed_json)
                                            : (selectedSection.section.content_json 
                                                ? JSON.stringify(selectedSection.section.content_json)
                                                : '')}
                                        onChange={() => {}} // Read-only, no changes allowed
                                        placeholder="Section content will appear here..."
                                        editable={false}
                                        searchQuery={searchQuery}
                                    />
                                </div>
                            )}

                            {isEditMode && sectionMetadata && (
                                <div className="review-section-metadata edit-mode">
                                    <h3 className="review-metadata-title">Section Metadata</h3>
                                    <div className="review-metadata-grid">
                                        <div className="review-metadata-field">
                                            <label>Visibility</label>
                                            <select
                                                value={sectionMetadata.visibility || ''}
                                                onChange={(e) => setSectionMetadata({
                                                    ...sectionMetadata,
                                                    visibility: e.target.value as VisibilityType
                                                })}
                                            >
                                                <option value="">Inherit from content source</option>
                                                {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="review-metadata-field">
                                            <label>Ref Visibility</label>
                                            <select
                                                value={sectionMetadata.ref_visibility}
                                                onChange={(e) => setSectionMetadata({
                                                    ...sectionMetadata,
                                                    ref_visibility: e.target.value as VisibilityType
                                                })}
                                            >
                                                {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="review-metadata-field">
                                            <label>Review Status</label>
                                            <select
                                                value={sectionMetadata.review_status}
                                                onChange={(e) => setSectionMetadata({
                                                    ...sectionMetadata,
                                                    review_status: e.target.value as ReviewStatusType
                                                })}
                                            >
                                                {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="review-metadata-field review-metadata-field-full">
                                            <label>Filter Tags</label>
                                            <input
                                                type="text"
                                                value={sectionMetadata.filter_tags.join(', ')}
                                                onChange={(e) => setSectionMetadata({
                                                    ...sectionMetadata,
                                                    filter_tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                                                })}
                                                placeholder="Comma-separated tags"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="review-editor-empty">
                            {/* <div className="review-editor-empty-icon">📄</div> */}
                            {/* <h3>Select a section to review</h3> */}
                            <p>Use the search bar to search through the sections</p>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDialog
                isOpen={dialogState.isOpen}
                onConfirm={dialogState.onConfirm}
                onCancel={dialogState.onCancel}
                title={dialogState.title}
                message={dialogState.message}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                isDestructive={dialogState.isDestructive}
            />
    </div>
  );
};

export default ReviewEditor;
